import { useEffect, useRef, useState } from "react";

/**
 * 모바일 가상 키보드가 열렸을 때 visual viewport 기준 하단 inset(px)을 반환합니다.
 */
export function useKeyboardInset() {
  const [keyboardInset, setKeyboardInset] = useState(0);
  const maxInnerHeightRef = useRef(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const MIN_KEYBOARD_HEIGHT = 120;
    const viewport = window.visualViewport;
    maxInnerHeightRef.current = window.innerHeight;

    const isEditableElementFocused = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) {
        return false;
      }

      const tagName = activeElement.tagName.toLowerCase();
      const isEditableTag = tagName === "input" || tagName === "textarea";
      const isContentEditable = activeElement.isContentEditable;

      return isEditableTag || isContentEditable;
    };

    const updateKeyboardInset = () => {
      const currentInnerHeight = window.innerHeight;
      maxInnerHeightRef.current = Math.max(maxInnerHeightRef.current, currentInnerHeight);

      // 입력 요소에서 포커스가 빠지면 키보드는 닫히는 중이거나 이미 닫힌 상태다.
      // iOS Safari는 키보드가 내려가는 애니메이션이 끝난 뒤에야 visualViewport.height를
      // 되돌려주기 때문에, 뷰포트 값만 기다리면 버튼이 한 박자 늦게 내려온다.
      if (!isEditableElementFocused()) {
        setKeyboardInset((prev) => (prev === 0 ? prev : 0));
        return;
      }

      // visualViewport를 지원하면 이 값만으로 키보드가 가린 높이를 정확히 알 수 있다.
      // innerHeight 축소량으로 추정하는 방식은 innerHeight가 키보드 외의 이유
      // (브라우저 툴바 변화, DevTools 디바이스 모드의 뷰포트 전환 등)로 줄어들 때도
      // 키보드가 열린 것처럼 오인해 버튼을 과도하게 밀어올리므로 폴백으로만 사용한다.
      if (viewport) {
        const nextInset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
        setKeyboardInset((prev) => (Math.abs(prev - nextInset) > 1 ? nextInset : prev));
        return;
      }

      const resizeBasedInset = Math.max(0, maxInnerHeightRef.current - currentInnerHeight);
      const nextInset =
        isEditableElementFocused() && resizeBasedInset > MIN_KEYBOARD_HEIGHT ? resizeBasedInset : 0;
      setKeyboardInset((prev) => (Math.abs(prev - nextInset) > 1 ? nextInset : prev));
    };

    const scheduleKeyboardInsetUpdate = () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = requestAnimationFrame(() => {
        updateKeyboardInset();
      });
    };

    const handleOrientationChange = () => {
      maxInnerHeightRef.current = window.innerHeight;
      scheduleKeyboardInsetUpdate();

      window.setTimeout(() => {
        maxInnerHeightRef.current = Math.max(maxInnerHeightRef.current, window.innerHeight);
        scheduleKeyboardInsetUpdate();
      }, 350);
    };

    scheduleKeyboardInsetUpdate();
    window.addEventListener("resize", scheduleKeyboardInsetUpdate);
    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("focusin", scheduleKeyboardInsetUpdate);
    window.addEventListener("focusout", scheduleKeyboardInsetUpdate);

    if (viewport) {
      viewport.addEventListener("resize", scheduleKeyboardInsetUpdate);
      viewport.addEventListener("scroll", scheduleKeyboardInsetUpdate);
    }

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
      window.removeEventListener("resize", scheduleKeyboardInsetUpdate);
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("focusin", scheduleKeyboardInsetUpdate);
      window.removeEventListener("focusout", scheduleKeyboardInsetUpdate);

      if (viewport) {
        viewport.removeEventListener("resize", scheduleKeyboardInsetUpdate);
        viewport.removeEventListener("scroll", scheduleKeyboardInsetUpdate);
      }
    };
  }, []);

  return keyboardInset;
}
